import React, { useMemo, useState } from 'react';
import { ThumbsUp, ThumbsDown } from 'lucide-react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { FileType } from '../../../types';
import { apiV2 } from '../../../lib/baseAxios';
import { RootState } from '../../../reducers';
import FileList from './FileList';
import { Button } from '../../../components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../../components/ui/tooltip';
import { cn } from '../../../lib/utils';

export type AssistantFileType = FileType & {
  fileId?: string;
  fileName?: string;
};

interface SearchResultsProps {
  results: {
    data: AssistantFileType[];
    error: string | null;
  };
  isFileListExpanded: boolean;
  toggleFileList: () => void;
  fileThumbnails: Array<{
    fileId: string;
    thumbnailUrl: string | null;
    isLoading: boolean;
  }>;
  setFileThumbnails: React.Dispatch<
    React.SetStateAction<
      Array<{
        fileId: string;
        thumbnailUrl: string | null;
        isLoading: boolean;
      }>
    >
  >;
  handleClose: () => void;
  isLoading?: boolean;
  query?: string;
  aiSummary?: string;
}

const SearchResults: React.FC<SearchResultsProps> = ({
  results,
  isFileListExpanded,
  toggleFileList,
  fileThumbnails,
  setFileThumbnails,
  isLoading = false,
  query = '',
  aiSummary = '',
}) => {
  const { t } = useTranslation();
  const userSettings = useSelector((state: RootState) => state.settings.user);

  const [feedbackState, setFeedbackState] = useState<'up' | 'down' | null>(
    null,
  );

  const citedFiles = useMemo(() => {
    const seenIds = new Set<string>();
    const seenNames = new Set<string>();
    const uniqueResults = results.data
      .filter((file) => {
        const id = file.fileId || file._id;
        const name = file.fileName || file.name;

        if (!id || !name) {
          return false;
        }

        if (seenIds.has(id) || seenNames.has(name)) {
          return false;
        }

        seenIds.add(id);
        seenNames.add(name);
        return true;
      })
      .map((file) => ({
        ...file,
        fileId: file.fileId || file._id,
        fileName: file.fileName || file.name,
      }));

    return uniqueResults;
  }, [results.data]);

  const getFileIds = () =>
    results.data
      .map((file) => file.fileId || file._id)
      .filter(Boolean)
      .slice(0, 10);

  const handleFeedback = async (value: 1 | -1) => {
    try {
      const fileIds = getFileIds();
      await apiV2.post('/feedbackcounts', {
        feature: 'search',
        value,
        userId: userSettings._id,
        metadata: {
          query,
          results: {
            count: results.data.length,
            fileIds,
          },
          summaryText: aiSummary,
        },
      });
      console.log(
        `Feedback submitted: ${value === 1 ? 'thumbs up' : 'thumbs down'}`,
      );
    } catch (error) {
      console.error('Error submitting feedback:', error);
    }
  };

  const deleteFeedback = async () => {
    try {
      const fileIds = getFileIds();
      await apiV2.delete('/feedbackcounts', {
        data: {
          feature: 'search',
          userId: userSettings._id,
          query,
          fileIds,
        },
      });
      setFeedbackState(null);
      console.log('Feedback deleted');
    } catch (error) {
      console.error('Error deleting feedback:', error);
    }
  };

  const handleThumbsUpClick = () => {
    if (feedbackState === 'up') {
      deleteFeedback();
    } else {
      setFeedbackState('up');
      handleFeedback(1);
    }
  };

  const handleThumbsDownClick = () => {
    if (feedbackState === 'down') {
      deleteFeedback();
    } else {
      setFeedbackState('down');
      handleFeedback(-1);
    }
  };

  return (
    <div className="relative">
      {citedFiles.length > 0 && (
        <FileList
          files={citedFiles}
          isExpanded={isFileListExpanded}
          onToggleExpand={toggleFileList}
          fileThumbnails={fileThumbnails}
          setFileThumbnails={setFileThumbnails}
          isLoading={isLoading}
          label={t('assistant.search.results', { count: citedFiles.length })}
        />
      )}

      {citedFiles.length === 0 && (
        <p className="text-sm text-muted-foreground p-2">
          {t('assistant.search.noResults')}
        </p>
      )}

      {results.data.length > 0 && (
        <div className="absolute bottom-2 right-2 flex gap-2 z-10">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleThumbsUpClick}
                  aria-label={t('assistant.search.feedback.helpful')}
                  className={cn(
                    'w-8 h-8',
                    feedbackState === 'up'
                      ? 'text-green-600 hover:text-green-700'
                      : 'text-muted-foreground hover:text-green-600',
                  )}
                >
                  <ThumbsUp
                    className="w-5 h-5"
                    fill={feedbackState === 'up' ? 'currentColor' : 'none'}
                  />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>{t('assistant.search.feedback.helpful')}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>

          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleThumbsDownClick}
                  aria-label={t('assistant.search.feedback.notHelpful')}
                  className={cn(
                    'w-8 h-8',
                    feedbackState === 'down'
                      ? 'text-red-600 hover:text-red-700'
                      : 'text-muted-foreground hover:text-red-600',
                  )}
                >
                  <ThumbsDown
                    className="w-5 h-5"
                    fill={feedbackState === 'down' ? 'currentColor' : 'none'}
                  />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>{t('assistant.search.feedback.notHelpful')}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      )}
    </div>
  );
};

export default SearchResults;
